import { GridStateFactory } from '@b2b/common/factories/gridState/types';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { BasePriceFilterModule } from '@b2b/common/filters';
import { ThemesData } from '@b2b/common/resolvers';
import { ApiService } from '@b2b/common/services/api/types';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import {
  ACMEResponse,
  ColumnDef,
  ContentHeaderProps,
  extractACMEErrorMessage,
  TODO,
  UIBModalService,
} from '@b2b/types/core';
import uiRouter from '@uirouter/angularjs';
import angular, { IFilterService, IPromise, IQService, IScope, ITimeoutService } from 'angular';
import { InventoryItem } from '../types';
import { InventoryCreateModalControllerModule, InventoryCreateModalData } from './InventoryCreateModalController';
import { InventoryRemoveModalControllerModule } from './InventoryRemoveModalController';
import { NoticesComponentModule } from '@b2b/common/components/notices';

InventoryListController.$inject = [
  '$scope',
  '$filter',
  '$timeout',
  '$uibModal',
  '$q',
  'ApiService',
  'PermissionsService',
  'HelpersService',
  'TemplatesService',
  'NoticeFactory',
  'GridStateFactory',
  'ThemesHelperService',
  'ThemesData',
];

function InventoryListController(
  $scope: IScope & { inventoryList: TODO },
  $filter: IFilterService,
  $timeout: ITimeoutService,
  $uibModal: UIBModalService,
  $q: IQService,
  ApiService: ApiService,
  PermissionsService: PermissionsService,
  HelpersService: TODO,
  TemplatesService: TODO,
  NoticeFactory: NoticeFactory,
  GridStateFactory: GridStateFactory,
  ThemesHelperService: ThemesHelperService,
  ThemesData: { data: ACMEResponse<ThemesData> }
) {
  const ctrl = {
    grid: {
      multiSelect: false,
      pageSize: 50,
      paginate: true,
      totalItems: 0,
      showFooter: true,
      enableRowSelection: false,
      useExternalPagination: true,
      useExternalSorting: true,
      columnDefs: [
        {
          field: 'id',
          visible: false,
        },
        {
          field: 'order',
          visible: false,
        },
        {
          field: 'name',
          displayName: 'Name',
          editableCellTemplate: 'common/includes/cellEditorTemplate.tpl.html',
        },
        {
          field: 'description',
          displayName: 'Description',
          editableCellTemplate: 'common/includes/cellEditorTemplate.tpl.html',
        },
        {
          field: 'sku',
          displayName: 'SKU',
          editableCellTemplate: 'common/includes/cellEditorTemplate.tpl.html',
        },
        {
          field: 'type',
          displayName: 'Type',
          cellTemplate: `<div class="ui-grid-cell-contents">{{ row.entity.type | typeToLabel }}</div>`,
        },
        {
          field: 'printTicket',
          displayName: 'Print Ticket',
          cellTemplate: `<div class="ui-grid-cell-contents">{{ (row.entity.printTicket) ? "Yes" : "No" }}</div>`,
        },
        {
          field: 'price',
          displayName: 'Price',
          cellFilter: 'currency',
          editableCellTemplate: 'common/includes/cellEditorTemplate.tpl.html',
        },
        {
          field: 'salesChannels[0].enabled',
          displayName: 'Web',
          cellTemplate: `<div data-ng-class="\'colt\' + col.index" class="grid-input-cell"><span data-ng-if="COL_FIELD">Yes</span><span data-ng-if="!COL_FIELD">No</span></div>`,
        },
        {
          field: 'salesChannels[1].enabled',
          displayName: 'POS',
          cellTemplate: `<div data-ng-class="\'colt\' + col.index" class="grid-input-cell"><span data-ng-if="COL_FIELD">Yes</span><span data-ng-if="!COL_FIELD">No</span></div>`,
        },
        {
          field: 'salesChannels[2].enabled',
          displayName: 'Reseller',
          cellTemplate: `<div data-ng-class="\'colt\' + col.index" class="grid-input-cell"><span data-ng-class="{ inactive: grid.appScope.programsDetailOverview.restrictMembers }" data-ng-if="COL_FIELD">Yes</span><span data-ng-if="!COL_FIELD">No</span></div>`,
        },
        {
          field: 'salesChannels[3].enabled',
          displayName: 'Customer Rep.',
          cellTemplate: `<div data-ng-class="\'colt\' + col.index" class="grid-input-cell"><span data-ng-if="COL_FIELD">Yes</span><span data-ng-if="!COL_FIELD">No</span></div>`,
        },
        {
          field: 'salesChannels[5].enabled',
          displayName: 'Kiosk',
          cellTemplate: `<div data-ng-class="\'colt\' + col.index" class="grid-input-cell"><span data-ng-if="COL_FIELD">Yes</span><span data-ng-if="!COL_FIELD">No</span></div>`,
        },
      ] as ColumnDef[],
    },
    model: [] as InventoryItem[],
    filtered: [] as InventoryItem[],
    edited: {},

    searchQuery: '',
    searchTimer: null as IPromise<void> | null,

    editTimer: false,
    isEditing: false,
    isUpdating: false,
    enableSalesTax: false,

    notice: new NoticeFactory(),
    gridState: new GridStateFactory(),

    init: function () {
      if (ThemesData && ThemesData.data.success) {
        ThemesHelperService.setModel(ThemesData.data.payload);

        ctrl.enableSalesTax = ThemesHelperService.getFeatureFlag('enableSalesTax');

        if (ctrl.enableSalesTax) {
          const priceColumnIndex = ctrl.grid.columnDefs.findIndex((column) => column.field === 'price');

          /**
           * Insert after `basePrice` after `price` column
           */
          ctrl.grid.columnDefs.splice(priceColumnIndex + 1, 0, {
            field: 'basePrice',
            displayName: 'Base Price',
            cellFilter: 'basePrice',
            editableCellTemplate: 'common/includes/cellEditorTemplate.tpl.html',
          });
        }
      }

      $scope.$emit('Application::contentTitleChange', {
        title: 'Add-ons',
        buttonRight: {
          permissions: ['ADDONS_CREATE'],
          label: 'New Add-on',
          action: () => {
            ctrl.create();
          },
        },
      } as ContentHeaderProps);

      ctrl.gridState.updateGridState(null, ctrl.grid.pageSize, 'name', 'asc');
      ctrl.getInventoryList(ctrl.gridState.getGridState());

      if (PermissionsService.check('ADDONS_DELETE')) {
        ctrl.grid.columnDefs.push({
          field: 'remove',
          displayName: 'Remove',
          width: 80,
          onCellNavClick: (row, col) => {
            ctrl.remove(row.entity.id);
          },
          cellTemplate: `<div class="ui-grid-cell-contents" ng-class="col.colIndex()"><a data-ng-click="grid.appScope.remove(row.entity.id)" role="button">Remove<span class="sr-only">-Opens a dialog</span></a></div>`,
        });
      }

      if (PermissionsService.check('ADDONS_UPDATE')) {
        ctrl.grid.columnDefs.unshift({
          field: 'edit',
          displayName: 'Edit',
          width: 60,
          onCellNavClick: (row, col) => {
            ctrl.edit(row.entity.id);
          },
          cellTemplate: `<div class="ui-grid-cell-contents" ng-class="col.colIndex()"><a data-ng-click="grid.appScope.edit(row.entity.id)" role="link">Edit<span class="sr-only"> Details for {{ row.entity.name }}</span></a></div>`,
        });
      }

      $scope.$on('$destroy', $scope.$watch('inventoryList.searchQuery', ctrl.onSearchQueryUpdate));
    },

    // TODO: possibly deprecated by the typeToLabel filter in this module
    getTypeLabel: function (type) {
      if (type === 'doNotTrack') {
        return 'Standard';
      } else if (type === 'donation') {
        return 'Variable Donation';
      } else {
        return ($filter('inflector') as TODO)(type, 'humanize'); // TODO: what is 'inflector' used for?
      }
    },

    getInventoryList: function (query: TODO) {
      ApiService.inventory
        .list(angular.extend({ addonTypes: 'doNotTrack' }, query))
        .then((response) => {
          const inventory = response.data.payload;

          ctrl.model = ctrl.processSalesChannels(inventory.list);
          ctrl.isUpdating = false;
          ctrl.filtered = angular.copy(ctrl.model);
          //wait for next tick
          ctrl.isUpdating = false;
          $timeout(() => {
            ctrl.grid.totalItems = inventory.pagination.count;
          }, 0);
          if (!$scope.$$phase) {
            $scope.$apply();
          }
        })
        .catch(() => {
          // noop
        });
    },

    hasItems: function () {
      return ctrl.filtered instanceof Array && ctrl.filtered.length > 0;
    },

    findInventory: function (id: number) {
      const index = ctrl.model.findIndex((item) => item.id === id);
      return index === -1 ? undefined : { index, item: ctrl.model[index] };
    },

    removeInventory: function (id) {
      const found = ctrl.findInventory(id);

      if (found) {
        ctrl.model.splice(found.index, 1);
      }

      ctrl.search();
    },

    search: function () {
      ctrl.filtered = $filter('filter')(ctrl.model, function (inventoryItem: TODO) {
        const name = inventoryItem.name + '|' + inventoryItem.sku;
        const value = name.toLowerCase();
        return value.indexOf(ctrl.searchQuery.toLowerCase()) !== -1 ? inventoryItem : null;
      });
    },

    update: function (item: InventoryItem) {
      ApiService.inventory
        .update(item)
        .then((response) => {
          ctrl.edited[response.data.payload.id] = false;

          const found = ctrl.findInventory(response.data.payload.id);

          if (found) {
            ctrl.model[found.index] = ctrl.processSalesChannels([response.data.payload])[0];
            ctrl.filtered[found.index] = angular.copy(ctrl.model[found.index]);
          }

          ctrl.getInventoryList({ ...ctrl.gridState.getGridState(), search: ctrl.searchQuery });

          $timeout(() => {
            ctrl.isUpdating = false;
            ctrl.isEditing = false;
          }, 500);
        })
        .catch((response) => {
          ctrl.notice.showErrorMessages([{ message: extractACMEErrorMessage(response.data) }], { duration: 3000 });

          $timeout(() => {
            ctrl.isUpdating = false;
          }, 500);
        });
    },

    remove: function (id) {
      const found = ctrl.findInventory(id);

      $uibModal
        .open<true>({
          templateUrl: 'views/application/views/inventory/list/modals/removeInventory.tpl.html',
          size: 'md',
          controller: 'InventoryRemoveModalController',
          controllerAs: '$ctrl',
          resolve: {
            currentInventory: () => found?.item ?? null,
          },
        })
        .result.then(() => {
          ctrl.notice.hideSuccessMessages();
          ctrl.notice.hideErrorMessages();

          ctrl.isUpdating = true;

          ApiService.inventory
            .remove(id)
            .then(() => {
              let query = ctrl.gridState.getGridState();
              query = HelpersService.decorate(query, { search: ctrl.searchQuery });

              ctrl.getInventoryList(query);
            })
            .catch(() => {
              $timeout(() => {
                ctrl.isUpdating = false;
              }, 500);
            });
        })
        .catch(() => {
          // noop
        });
    },

    create: function () {
      $uibModal
        .open<InventoryItem>({
          templateUrl: 'views/application/views/inventory/list/modals/createInventory.tpl.html',
          size: 'lg',
          controller: 'InventoryCreateModalController',
          controllerAs: '$ctrl',
          resolve: {
            MODAL_DATA: () => {
              return {
                enableSalesTax: ctrl.enableSalesTax,
                inventory: ctrl.model,
                currentInventory: null,
              } satisfies InventoryCreateModalData;
            },
          },
        })
        .result.then((inventory) => {
          ctrl.notice.hideSuccessMessages();
          ctrl.notice.hideErrorMessages();
          ctrl.isUpdating = true;

          ApiService.inventory
            .create(inventory)
            .then(() => {
              let query = ctrl.gridState.getGridState();

              query = HelpersService.decorate(query, { search: ctrl.searchQuery, addonTypes: 'doNotTrack' });

              ctrl.getInventoryList(query);
            })
            .catch((response) => {
              ctrl.notice.showErrorMessages([{ message: extractACMEErrorMessage(response.data) }], { duration: 3000 });

              $timeout(() => {
                ctrl.isUpdating = false;
              }, 500);
            });
        })
        .catch(() => {
          // noop
        });
    },

    edit: function (id) {
      const found = angular.copy(ctrl.findInventory(id));

      $uibModal
        .open<InventoryItem>({
          templateUrl: 'views/application/views/inventory/list/modals/createInventory.tpl.html',
          size: 'lg',
          controller: 'InventoryCreateModalController',
          controllerAs: '$ctrl',
          resolve: {
            MODAL_DATA: () => {
              return {
                enableSalesTax: ctrl.enableSalesTax,
                inventory: ctrl.model,
                currentInventory: found?.item ?? null,
              } satisfies InventoryCreateModalData;
            },
          },
        })
        .result.then((inventory) => {
          ctrl.notice.hideSuccessMessages();
          ctrl.notice.hideErrorMessages();
          ctrl.isUpdating = true;
          ctrl.update(inventory);
        })
        .catch(() => {
          // noop
        });
    },

    onDataGridPaginate: function (newPageNumber, pageSize) {
      let query = {};

      ctrl.gridState.updateGridState(newPageNumber, pageSize);

      query = HelpersService.decorate(query, ctrl.gridState.getGridState());
      query = HelpersService.decorate(query, { search: ctrl.searchQuery });

      ctrl.getInventoryList(query);
    },

    onDataGridSortChange: function (grid, columns) {
      const data = columns[0];
      let query = {};

      if (data) {
        ctrl.gridState.updateGridState(null, null, data.field, data.sort.direction);
      }

      query = HelpersService.decorate(query, ctrl.gridState.getGridState());
      query = HelpersService.decorate(query, { search: ctrl.searchQuery });

      ctrl.getInventoryList(query);
    },

    processSalesChannels: function (list) {
      for (let i = 0, ii = list.length; i < ii; i++) {
        list[i].salesChannels = TemplatesService.orderSalesChannels(list[i].salesChannels);
      }

      return list;
    },

    onGridDblClick: function (item) {
      ctrl.edited[item.entity.id] = true;
    },

    onSearchQueryUpdate: function (n, o) {
      let query = ctrl.gridState.getGridState();

      if (n !== o) {
        query = HelpersService.decorate(query, { search: ctrl.searchQuery });

        if (ctrl.searchTimer) {
          $timeout.cancel(ctrl.searchTimer);
        }

        ctrl.searchTimer = $timeout(() => {
          ctrl.getInventoryList(query);
        }, 250);
      }
    },
  };

  $scope.inventoryList = ctrl;
  ctrl.init();
}

export const InventoryListControllerModule = angular
  .module('acme.view.application.inventory.list.controllers', [
    uiRouter,
    'ui.bootstrap',
    'ui.inflector',
    'acme.common.service.api',
    'acme.common.service.helpers',
    'acme.common.service.templates',
    'acme.common.service.permissions',
    'acme.common.factory.notice',
    'acme.common.factory.gridState',
    'acme.common.service.themesHelper',
    InventoryCreateModalControllerModule,
    InventoryRemoveModalControllerModule,
    BasePriceFilterModule,
    NoticesComponentModule,
  ])
  .controller('InventoryListController', InventoryListController)
  .filter('typeToLabel', [
    '$filter',
    function ($filter: IFilterService) {
      return function (text) {
        if (text === 'doNotTrack') {
          return 'Standard';
        } else if (text === 'donation') {
          return 'Variable Donation';
        } else {
          return ($filter('inflector') as TODO)(text, 'humanize'); // TODO: what is 'inflector' used for?
        }
      };
    },
  ]).name;
